// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import ClearIcon from '@mui/icons-material/Clear';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  AppBar,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Grow,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
  Toolbar,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import DialogTransition from '../Common/Transitions/DialogTransition';
import { ReactComponent as HelpOutlineIcon } from '../../assets/images/help-circle.svg';
import { ReactComponent as InventoryIcon } from '../../assets/images/InventoryIcon.svg';
import NimbusUno from '../../assets/images/NIMBUS Uno.svg';
import { ReactComponent as ApplicationIcon } from '../../assets/images/ApplicationIcon.svg';
import { ReactComponent as LogoutIcon } from '../../assets/images/LogoutIcon.svg';
import { ReactComponent as MRMVault } from '../../assets/images/MRMVault.svg';
import useColorPallete from '../../Hooks/Settings/useColorPallete';
import useCommon from '../../Hooks/useCommon';
import useTaskManager from '../../Hooks/TaskManager/useTaskManager';
import utils from '../../utils/utils';
import HelpSectionDrawer from '../../views/HelpSection/HelpSectionDrawer';
import { SwalToast } from '../Common';
import CreateEditProjectModal from '../Project/CreateEditProjectModal';
import NotificationDrawer from '../../views/Notifications/NotificationDrawer';

const imgStyle = {
  width: '87.5px',
  height: '40px',
  objectFit: 'contain',
};
const FOCUS_VISIBLE_COLOR = '2px solid white';
const COLOR = 'primary.main';
const BADGE_FOCUS = 'other.badgeFocusColor';
const LOGOUT_CONFIRMATION_MESSAGE =
  'Access to this project is restricted to a different account. For security purposes, you will be logged out automatically. Please log in with the appropriate account to continue accessing the project.';
const isOptionEqualToValue = (option, _value) => {
  return option.project_id === _value.project_id;
};

const getOptionLabel = (_project) => {
  if (_project && _project.name) {
    return _project.name;
  }
  return '';
};

const LogoutConfirmModal = ({ open, onLogoutClick, confirmationMsg }) => (
  <Dialog
    open={open}
    aria-labelledby="delete-dialog"
    maxWidth="xs"
    fullWidth
    TransitionComponent={DialogTransition}
  >
    <DialogTitle id="delete-dialog">Attention</DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            align="center"
            sx={{
              wordWrap: 'break-word',
              '&:focus-visible': {
                border: '2px solid',
                borderColor: 'primary.main',
              },
            }}
            tabIndex={0}
            role="note"
            aria-label={confirmationMsg}
          >
            {confirmationMsg}
          </Typography>
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button
        color="primary"
        variant="contained"
        title="ok"
        aria-label="ok"
        onClick={onLogoutClick}
      >
        OK
      </Button>
    </DialogActions>
  </Dialog>
);

LogoutConfirmModal.propTypes = {
  onLogoutClick: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  confirmationMsg: PropTypes.string.isRequired,
};

const data123 = [
  {
    id: 1,
    message: 'Task has been created successfully.',
    created_at: '2024-01-01T10:00:00Z',
  },
  {
    id: 2,
    message: 'Task has been updated.',
    created_at: '2024-01-02T14:30:00Z',
  },
  {
    id: 3,
    message: 'New comment added to your task.',
    created_at: '2024-01-03T09:15:00Z',
  },
  {
    id: 4,
    message: 'Task deadline is approaching.',
    created_at: '2024-01-04T08:00:00Z',
  },
  {
    id: 5,
    message: 'Task has been marked as completed.',
    created_at: '2024-01-05T16:45:00Z',
  },
  {
    id: 6,
    message: 'Task has been marked as completed.',
    created_at: '2024-01-05T16:45:00Z',
  },
  {
    id: 7,
    message: 'Task has been marked as completed.',
    created_at: '2024-01-05T16:45:00Z',
  },
  {
    id: 8,
    message: 'Task has been marked as completed.',
    created_at: '2024-01-05T16:45:00Z',
  },
];

/**
 * @function AppHeader
 * @description Header of the application
 * @param {object} project - project
 * @param {function} fetchUserProjects - fetch users projects
 * @param {function} createNewProject - create new project
 * @param {function} fetchSentReceivedArtifacts - fetch sent and received artifacts
 * @param {function} fetchSentReceivedProject - fetch sent and received projects
 * @param {function} fetchSentReceivedPipelines - fetch sent and received pipelines
 * @param {object} activateProject - active project
 * @param  {function} fetchDataSets - to fetch all the datasets
 * @param {object} user - user
 * @param {object} permission - permission
 * @param {object} fetchUserProjectsStatus - fetch user projects status
 * @returns {JSX} AppHeader UI
 * @example <AppHeader project={project}/>
 */
const AppHeader = ({
  project,
  fetchUserProjects,
  createNewProject,
  fetchSentReceivedArtifacts,
  fetchSentReceivedProject,
  fetchSentReceivedPipelines,
  activateProject,
  user,
  permission,
  fetchUserProjectsStatus,
  fetchProjectTypes,
  createCollaborativeProjectPermission,
}) => {
  const themes = useTheme();
  const { fetchUnseenNotificationCount } = useTaskManager();
  const navigate = useNavigate();
  // for AD only
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [inputValueProject, setInputValueProject] = useState('');
  const [value, setValue] = useState(project.activeProject);
  const [selectProjectOpen, setSelectProjectOpen] = useState(false);
  const [pendingArtifactsCount, setPendingArtifactsCount] = useState([]);
  const [pendingProjectCount, setPendingProjectCount] = useState([]);
  const [pendingPipelineCount, setpendingPipelineCount] = useState([]);
  const [isHelpIconClicked, setIsHelpIconClicked] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] =
    React.useState(false);
  const anchorRef = useRef();
  const inputRef = useRef();
  const appRef = useRef();
  const [open, setOpen] = useState(false);
  const [openApps, setOpenApps] = useState(false);
  const { common } = useCommon();
  const { getCurrentTheme, currentSavedTheme } = useColorPallete();
  const [appHeaderLogo, setAppHeaderLogo] = useState(undefined);

  const REFERER_VAULT = 'Vault';

  const appBarSx = useCallback(
    (theme) => ({ zIndex: theme.zIndex.drawer + 1 }),
    []
  );

  const buttonSx = useCallback(
    (theme) => ({
      '&.Mui-disabled': {
        background: theme.palette.other.grey2,
        color: theme.palette.other.white2,
      },
      '&:focus-visible': {
        outline: FOCUS_VISIBLE_COLOR,
      },
    }),
    []
  );

  const autoCompleteSx = useCallback(
    (theme) => ({
      width: 200,
      outline: 0,
      height: '100%',
      borderRadius: '5px',
      background: 'rgba(174, 205, 252, 0.2)',
      color: theme.palette.other.white,
      '& .MuiAutocomplete-endAdornment': {
        right: '5px !important',
      },
      '& .MuiAutocomplete-popupIndicator': {
        ariaHidden: 'true',
      },
      '&:focus-within': {
        outline: FOCUS_VISIBLE_COLOR,
        borderRadius: '4px',
        backgroundColor: COLOR,
      },
    }),
    []
  );
  const textFieldSx = useCallback(
    (theme) => ({
      marginTop: 0,
      height: '40px',
      '& .MuiInputBase-root': {
        backgroundColor: 'transparent !important',
        color: theme.palette.other.white,
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.other.white,
      },
    }),
    []
  );
  const badgeSx = useCallback(
    (theme) => ({
      '& .MuiBadge-badge': {
        color: theme.palette.other.white,
        backgroundColor: theme.palette.other.badgeBorder,
        marginRight: '10px',
        marginTop: '10px',
      },
    }),
    []
  );

  /**
   * @function handleShowHelpSectionDrawer
   * @description Closes the help section by setting the help icon's clicked state to false.
   * @returns {void}
   * @example handleShowHelpSectionDrawer()
   */
  const handleShowHelpSectionDrawer = useCallback(() => {
    setIsHelpIconClicked(false);
  }, [setIsHelpIconClicked]);

  /**
   * @function updatePendingArtifactsCount
   * @description It updates state of the Pending Artifacts Count
   * @returns {void}
   * @example updatePendingArtifactsCount()
   */
  const updatePendingArtifactsCount = () => {
    fetchSentReceivedProject().then((res) => {
      const pendingData =
        res.data.received?.length >= 1
          ? res.data.received?.filter(
              (data) => data?.receiver_status === 'PENDING'
            )
          : [];
      setPendingProjectCount(pendingData);
    });
    fetchSentReceivedArtifacts((res) => {
      const pendingData =
        res?.data?.received?.length >= 1
          ? res?.data?.received.filter(
              (data) => data?.reciever_status === 'Pending'
            )
          : [];
      setPendingArtifactsCount(pendingData);
    });
    fetchSentReceivedPipelines().then((res) => {
      const pendingData =
        res.data.received?.length >= 1
          ? res.data.received?.filter(
              (data) => data?.receiver_status === 'Pending'
            )
          : [];
      setpendingPipelineCount(pendingData);
    });
  };

  /**
   * @function pendingArtifactsProjectAndPipelineCount
   * @description It counts the pending Artifacts, Projects and Pipelines
   * @returns {void}
   * @example pendingArtifactsProjectAndPipelineCount()
   */
  const pendingArtifactsProjectAndPipelineCount = [
    ...pendingArtifactsCount,
    ...pendingProjectCount,
    ...pendingPipelineCount,
  ];

  /**
   * @function handleArtifactStatusChange
   * @description It handles the Status change of Artifact
   * @returns {void}
   * @example handleArtifactStatusChange()
   */
  const handleArtifactStatusChange = () => {
    updatePendingArtifactsCount();
  };

  React.useEffect(() => {
    updatePendingArtifactsCount();

    window.addEventListener('artifactStatusChange', handleArtifactStatusChange);

    return () => {
      window.removeEventListener(
        'artifactStatusChange',
        handleArtifactStatusChange
      );
    };
  }, []);

  React.useEffect(() => {
    if (
      !project.activeProject &&
      localStorage?.USER_ACTIVE_PROJECT_LIST &&
      localStorage.getItem('AUTO_PROJECT_ACTIVATED') !== 'true'
    ) {
      const getIdProject = new Map(
        JSON.parse(localStorage?.USER_ACTIVE_PROJECT_LIST)
      );
      const userId = localStorage?.user_data
        ? JSON.parse(localStorage?.user_data)?.id
        : undefined;
      const activeUserIdDetails = getIdProject.get(userId) || false;
      if (activeUserIdDetails) {
        const activeProjectSavedState = JSON.parse(activeUserIdDetails);
        project?.userProjectsList?.forEach((prj) => {
          if (prj.project_id === Number(activeProjectSavedState.project_id)) {
            localStorage.setItem('ACTIVE_PROJECT', activeProjectSavedState);
            activateProject(prj);
            localStorage.setItem('AUTO_PROJECT_ACTIVATED', true);
          }
        });
      }
    }
  }, [project]);

  const showProjectsHandler = () => {
    inputRef.current.focus();
  };
  React.useEffect(() => {
    document.addEventListener('showProjects', showProjectsHandler, false);
    return () => {
      document.removeEventListener('showProjects', showProjectsHandler);
    };
  }, []);

  const [openCreateProjectModal, setOpenCreateProjectModal] = useState(false);
  /**
   * @function handleOpen
   * @description changing state of open variable from null  to true
   * @returns {void}
   * @example handleOpen()
   */
  const handleOpen = useCallback(() => {
    setOpenCreateProjectModal(true);
  }, [setOpenCreateProjectModal]);
  /**
   * @function handleClose
   * @description changing state of open variable from null  to false
   * @returns {void}
   * @example handleClose()
   */
  const handleClose = useCallback(() => {
    setOpenCreateProjectModal(false);
  }, [setOpenCreateProjectModal]);

  const debouncedFetchUserProjects = useCallback(
    debounce(() => {
      fetchUserProjects(
        [
          'project_id',
          'clone_status',
          'share_status',
          'name',
          'is_archived',
          'regulation',
          'user_id',
          'is_collaborative',
          'is_completed',
          'system_generated_flag',
          'system_name',
          'type',
        ].join()
      ).then();
    }, 1000),
    [fetchUserProjects]
  );

  useEffect(() => {
    if (!project?.userProjectsList?.length) {
      debouncedFetchUserProjects();
    }
    setInputValueProject(
      project?.activeProject?.name ? project.activeProject.name : ''
    );
    setValue(project?.activeProject ? project?.activeProject : '');
  }, [project?.activeProject?.name]);

  const findMatchedProject = (projectId) => {
    return project?.userProjectsList?.find(
      (prop) => prop?.project_id === projectId
    );
  };

  const getActiveProject = () => {
    return localStorage.getItem('ACTIVE_PROJECT')
      ? JSON.parse(localStorage.getItem('ACTIVE_PROJECT'))
      : null;
  };

  const activateMatchedProject = (matchedProject) => {
    if (
      matchedProject?.system_generated_flag &&
      matchedProject?.system_name === REFERER_VAULT
    ) {
      const activeProject = getActiveProject();
      if (matchedProject?.project_id !== activeProject?.project_id) {
        activateProject(matchedProject);
        SwalToast({
          icon: 'success',
          title: `Project ${matchedProject?.name} activated successfully.`,
        });
      }
    }
  };

  /**
   * @function handleProjectActivationFromVault
   * @description Activates a project based on URL parameters or logs out if no match is found.
   * @returns {void}
   * @example handleProjectActivationFromVault()
   */
  const handleProjectActivationFromVault = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const projectId = parseInt(searchParams.get('projectId'), 10);
    const referer = searchParams.get('referer');
    const autoActivateProject = searchParams.get('autoActivateProject');

    if (projectId && referer === REFERER_VAULT && autoActivateProject) {
      const matchedProject = findMatchedProject(projectId);

      if (matchedProject) {
        activateMatchedProject(matchedProject);
      } else {
        setShowLogoutModal(true);
      }
    }
  };

  useEffect(() => {
    if (!project?.userProjectsList?.length) return;
    handleProjectActivationFromVault();
  }, [project?.userProjectsList]);

  /**
   * @function handleToggle
   * @description Reversing state of prevOpen
   * @returns {void}
   * @example handleToggle()
   */
  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, [setOpen]);

  const handleToggleApps = useCallback(() => {
    setOpenApps((prevOpen) => !prevOpen);
  }, [setOpenApps]);

  /**
   * @function handleCloseMenu
   * @description Handling the close user
   * @param {object} event - event object
   * @returns {void}
   * @example handleCloseMenu(event)
   */
  const handleCloseMenu = useCallback(
    (event) => {
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    },
    [setOpen, anchorRef]
  );

  /**
   * @function helpSectionOnClick
   * @description Toggles the state of the help icon, indicating whether it has been clicked.
   * @returns {void}
   * @example helpSectionOnClick()
   */
  const helpSectionOnClick = useCallback(() => {
    setIsHelpIconClicked((state) => !state);
    handleCloseMenu();
  }, [setIsHelpIconClicked, handleCloseMenu]);

  const notifyOnClick = useCallback(() => {
    setNotificationDrawerOpen(!notificationDrawerOpen);
    handleCloseMenu();
  }, [setNotificationDrawerOpen, notificationDrawerOpen, handleCloseMenu]);

  const handleCloseApps = useCallback(
    (event) => {
      if (appRef?.current?.contains(event?.target)) {
        return;
      }
      setOpenApps(false);
    },
    [setOpenApps, appRef]
  );

  const handleCreateProject = useCallback(
    (values) => {
      navigate('/project/create', values);
    },
    [navigate]
  );

  /**
   * @function handleListKeyDown
   * @description Once the required condition is fulfilled it prevents the default action and -
   * - change the state to false
   * @param {object} event - event object
   * @returns {void}
   * @example handleListKeyDown event={event}
   */
  const handleListKeyDown = useCallback(
    (event) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        setOpen(false);
        setOpenApps(false);
      }
    },
    [setOpen, setOpenApps]
  );

  const handleArtifactRedirect = useCallback(() => {
    navigate('/project/sent-received-artifacts');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    utils.logout(isAuthenticated, instance);
  }, [isAuthenticated, instance]);

  useEffect(() => {
    setTimeout(() => {
      getCurrentTheme();
    }, 500);
  }, [common.triggerHeaderIconChange, common.triggerProductIconChange]);
  useEffect(() => {
    const updatedLogo = currentSavedTheme?.product_icon;
    const browserLogo = currentSavedTheme?.browser_icon;
    // const browserTitle = currentSavedTheme?.product_name;
    if (browserLogo) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = browserLogo;
    }
    // if (browserTitle) {
    //   document.title = browserTitle || 'Nimbus';
    // }
    if (updatedLogo && updatedLogo !== appHeaderLogo) {
      setAppHeaderLogo((prevLogo) => {
        if (prevLogo !== updatedLogo) {
          return updatedLogo;
        }
        return prevLogo;
      });
    }
  }, [setAppHeaderLogo, appHeaderLogo, currentSavedTheme]);
  useEffect(() => {
    fetchUnseenNotificationCount();
  }, []);

  /**
   * @function handleClearInputValue
   * @description Clears the input value for the project selection.
   */
  const handleClearInputValue = useCallback(() => {
    setInputValueProject('');
  }, [setInputValueProject]);

  const handleProjectOpen = useCallback(() => {
    setSelectProjectOpen(true);
  }, [setSelectProjectOpen]);

  const handleProjectClose = useCallback(() => {
    setSelectProjectOpen(false);
  }, [setSelectProjectOpen]);

  const handleAutoCompleteInputChange = useCallback(
    (_event, newInputValue) => {
      setInputValueProject(newInputValue);
    },
    [setInputValueProject]
  );

  const handleAutoCompleteChange = useCallback(
    (_event, newValue) => {
      setValue(newValue);
      project?.userProjectsList?.forEach((prj) => {
        if (
          newValue !== null &&
          prj?.project_id === Number(newValue?.project_id)
        ) {
          activateProject(prj);
          SwalToast({
            icon: 'success',
            title: `Project ${prj?.name} activated successfully.`,
          });
        }
      });
    },
    [setValue, project, activateProject]
  );

  const handleAutoCompleteRender = useCallback(
    (params) => (
      <TextField
        {...params}
        variant="filled"
        title={
          project.activeProject?.name
            ? project?.activeProject?.name
            : 'Search project'
        }
        placeholder={
          project.activeProject?.name
            ? project.activeProject.name
            : 'Search project'
        }
        inputRef={inputRef}
        sx={(theme) => ({
          marginTop: 0,
          height: '40px',
          '& .MuiInputBase-root': {
            backgroundColor: 'transparent !important',
            color: theme.palette.other.white,
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.other.white,
          },
        })}
        aria-haspopup="listbox"
        aria-expanded={!!inputValueProject}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {inputValueProject && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="clear"
                    onClick={handleClearInputValue}
                    sx={{
                      '&:focus-visible': {
                        outline: FOCUS_VISIBLE_COLOR,
                      },
                    }}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
    [inputRef, project, inputValueProject, textFieldSx]
  );
  const renderOption = useCallback(
    (props, option) => (
      <Box
        component="li"
        {...props}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'center',
        }}
        key={option?.project_id}
      >
        <Typography
          title={option?.name}
          variant="body2"
          sx={{
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option?.name}
        </Typography>
      </Box>
    ),
    []
  );

  const handleClickAwayListenerClickAway = useCallback(
    () => setOpen(false),
    [setOpen]
  );

  const handleAppClickAwayListenerClickAway = useCallback(
    () => setOpenApps(false),
    [setOpenApps]
  );

  const filteredProjectList = useMemo(() => {
    return (
      project?.userProjectsList?.filter(
        (option) =>
          !option.is_archived &&
          !['InProgress', 'Failed'].includes(option?.clone_status) &&
          !['InProgress', 'Failed'].includes(option?.share_status)
      ) || []
    );
  }, [project?.userProjectsList]);

  const handleAppSwitch = useCallback(() => {
    const realmConfig = JSON.parse(localStorage.getItem('REALM_CONFIG'));
    const realmId = localStorage.getItem('REALM_ID');
    const url = `${realmConfig?.integrationsUrls?.vault}?org=${realmId}&fromLauncher=true`;
    window.open(url, '_blank');
    handleCloseApps();
  }, [handleCloseApps]);

  return (
    <AppBar position="absolute" sx={appBarSx} elevation={2}>
      <Toolbar>
        <Box flexGrow={1} role="img" aria-label="App Header Logo">
          {appHeaderLogo ? (
            <img src={appHeaderLogo} alt="App Header Logo" style={imgStyle} />
          ) : (
            <img src={NimbusUno} alt="App Header Logo" style={imgStyle} />
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Button
            color="secondary"
            sx={buttonSx}
            variant="contained"
            title={
              permission !== 'Yes'
                ? 'You are not authorized to access this!'
                : 'Create project'
            }
            disableRipple
            disabled={permission !== 'Yes'}
            onClick={handleOpen}
            size="large"
          >
            + Create Project
          </Button>
          <Autocomplete
            open={selectProjectOpen}
            onOpen={handleProjectOpen}
            onClose={handleProjectClose}
            sx={autoCompleteSx}
            value={value}
            disableClearable={true}
            id="app_header_nav_bar_select_project"
            inputValue={inputValueProject}
            options={filteredProjectList}
            noOptionsText="No project available."
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onInputChange={handleAutoCompleteInputChange}
            onChange={handleAutoCompleteChange}
            renderInput={handleAutoCompleteRender}
            renderOption={renderOption}
            variant="outlined"
            size="small"
          />
          {utils.showApplicationLauncher() && (
            <Box>
              <IconButton
                ref={appRef}
                aria-controls={openApps ? 'fade-menu' : ''}
                aria-haspopup="true"
                onClick={handleToggleApps}
                aria-label="Apps"
                color="primary"
                title="Apps"
              >
                <ApplicationIcon
                  style={{
                    color: themes.palette.secondary.main,
                    width: 24,
                    height: 24,
                  }}
                />
              </IconButton>
              <Popper
                open={openApps}
                anchorEl={appRef.current}
                role={undefined}
                transition
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                      marginTop: '8px',
                      marginRight: '24px',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener
                        onClickAway={handleAppClickAwayListenerClickAway}
                      >
                        <MenuList
                          style={{ width: '200px' }}
                          autoFocusItem={openApps}
                          id="menu-list-grow"
                          // eslint-disable-next-line react/jsx-no-bind
                          onKeyDown={handleListKeyDown}
                        >
                          <MenuItem
                            onClick={handleAppSwitch}
                            data-url={window.CONFIG.MRM_VAULT_BASE_URL}
                          >
                            <MRMVault
                              style={{
                                color: themes.palette.primary.main,
                                width: 24,
                                height: 24,
                              }}
                            />
                            &nbsp;&nbsp; MRM Vault
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          )}
          <Box>
            <Badge
              badgeContent={pendingArtifactsProjectAndPipelineCount?.length}
              sx={badgeSx}
              title={
                pendingArtifactsProjectAndPipelineCount?.length
                  ? 'Pending artifacts/projects/pipelines'
                  : ''
              }
            >
              <IconButton
                onClick={handleArtifactRedirect}
                aria-label="Artifacts/Projects/Pipelines"
                sx={{
                  '&:hover, &:focus': {
                    backgroundColor: BADGE_FOCUS,
                  },
                  '&:focus-visible': {
                    outline: FOCUS_VISIBLE_COLOR,
                    backgroundColor: COLOR,
                  },
                }}
              >
                <InventoryIcon />
              </IconButton>
            </Badge>
          </Box>
          <Box>
            <IconButton
              id="app_header_navbar_people_btn"
              ref={anchorRef}
              aria-controls={open ? 'fade-menu' : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              aria-label={user?.userData?.username}
              color="primary"
              title={user?.userData?.username}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  color: COLOR,
                  backgroundColor: 'secondary.main',
                }}
              >
                {utils.getInitials(user?.userData?.username)}
              </Avatar>
            </IconButton>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom' ? 'center top' : 'center bottom',
                    marginTop: '8px',
                    marginRight: '24px',
                  }}
                >
                  <Paper>
                    <ClickAwayListener
                      onClickAway={handleClickAwayListenerClickAway}
                    >
                      <MenuList
                        style={{ width: '200px' }}
                        autoFocusItem={open}
                        id="menu-list-grow"
                        // eslint-disable-next-line react/jsx-no-bind
                        onKeyDown={handleListKeyDown}
                      >
                        <MenuItem
                          style={{
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            display: 'block',
                          }}
                          onClick={handleCloseMenu}
                          disabled
                        >
                          {user?.userData?.username}
                        </MenuItem>
                        <MenuItem
                          onClick={notifyOnClick}
                          title="Notifications"
                          aria-label="Notifications"
                        >
                          <NotificationsIcon
                            style={{ color: themes.palette.primary.main }}
                            sx={{ width: 24, height: 24 }}
                          />
                          &nbsp;&nbsp; Notifications &nbsp;&nbsp;
                          <Box
                            sx={{
                              borderRadius: '50%',
                              backgroundColor: themes.palette.other.badgeBorder,
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginLeft: 'auto',
                            }}
                          >
                            <Typography
                              sx={{ color: themes.palette.other.badgeText }}
                              variant="caption"
                              title={common?.unseenNotificationCount}
                              aria-label={common?.unseenNotificationCount}
                            >
                              {common?.unseenNotificationCount}
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          onClick={helpSectionOnClick}
                          title="Support"
                          aria-label="Support"
                        >
                          <HelpOutlineIcon
                            color="primary"
                            sx={{ width: 24, height: 24 }}
                          />
                          &nbsp;&nbsp; Support
                        </MenuItem>
                        <MenuItem
                          onClick={handleLogout}
                          title="logout"
                          aria-label="logout"
                        >
                          <LogoutIcon
                            style={{ color: themes.palette.primary.main }}
                            sx={{
                              width: 24,
                              height: 24,
                            }}
                          />
                          &nbsp;&nbsp; Logout
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        </Box>
      </Toolbar>
      {openCreateProjectModal && (
        <CreateEditProjectModal
          open={openCreateProjectModal}
          handleOpen={handleOpen}
          handleClose={handleClose}
          onCreateProject={handleCreateProject}
          createNewProject={createNewProject}
          fetchProjects={fetchUserProjects}
          onClose={handleClose}
          fetchUserProjectsStatus={fetchUserProjectsStatus}
          fetchProjectTypes={fetchProjectTypes}
          fromAppHeader={true}
          createCollaborativeProjectPermission={
            createCollaborativeProjectPermission
          }
        />
      )}
      {isHelpIconClicked && (
        <HelpSectionDrawer
          setShowHelpSectionDrawer={handleShowHelpSectionDrawer}
          showHelpSectionDrawer={isHelpIconClicked}
        />
      )}
      {showLogoutModal && (
        <LogoutConfirmModal
          open={showLogoutModal}
          onLogoutClick={handleLogout}
          confirmationMsg={LOGOUT_CONFIRMATION_MESSAGE}
        />
      )}
      {notificationDrawerOpen && (
        <NotificationDrawer
          open={notificationDrawerOpen}
          setOpen={setNotificationDrawerOpen}
          data={data123}
        />
      )}
    </AppBar>
  );
};
AppHeader.defaultProps = {
  permission: '',
};
AppHeader.propTypes = {
  project: PropTypes.oneOfType([PropTypes.object]).isRequired,
  fetchUserProjects: PropTypes.func.isRequired,
  activateProject: PropTypes.func.isRequired,
  fetchDatasets: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.object]).isRequired,
  fetchSentReceivedArtifacts: PropTypes.func.isRequired,
  fetchSentReceivedProject: PropTypes.func.isRequired,
  fetchSentReceivedPipelines: PropTypes.func.isRequired,
  createNewProject: PropTypes.func.isRequired,
  permission: PropTypes.string,
  fetchUserProjectsStatus: PropTypes.func.isRequired,
  fetchProjectTypes: PropTypes.func.isRequired,
  createCollaborativeProjectPermission: PropTypes.string.isRequired,
};

export default AppHeader;
